// Quote Notes Summary — extracted from DeliveryPanel (Batch 1 rework).
// Renders the customer-facing summary block (what appears on the PDF)
// PLUS exports `buildQuoteSnapshot` — a formatter used by the manual
// "Save quote" action to append a timestamped snapshot into StaffNotes.
//
// Autosave never triggers a snapshot. The snapshot is a deliberate act
// of record-keeping tied to the orange Save button.

import { FileText, Info, Copy } from "lucide-react";
import { toast } from "sonner";
import { T2Panel, T2Label } from "../_t2";
import { suggestVehicle } from "./_buildTypeContent";
import { GBP, quoteCompletion, completionTone, websiteDiscount, onsiteAnswer, onsiteLabel } from "../_quoteHelpers";

export default function QuoteNotesSummaryPanel({ quote, delivery, subtotal, onChange, billing }) {
  const vehicle = suggestVehicle(delivery?.estimatedWeightKg);
  const includeOnPdf = !!delivery?.includeSummaryOnPdf;
  const patch = (p) => onChange({ ...(delivery || {}), ...p });

  const { summaryText } = buildSummaryStrings(quote, delivery, vehicle, billing);
  const comp = quoteCompletion(quote, delivery || {}, billing);
  const missing = comp.missing;

  const copyAll = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(summaryText);
      } else {
        throw new Error("no clipboard api");
      }
      toast.success("Summary copied — paste into WhatsApp or email");
    } catch {
      // Fallback for sandboxed iframes / older browsers where the async API is blocked.
      try {
        const ta = document.createElement("textarea");
        ta.value = summaryText;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (ok) toast.success("Summary copied — paste into WhatsApp or email");
        else toast.error("Couldn't copy — select the text and copy manually");
      } catch {
        toast.error("Couldn't copy — select the text and copy manually");
      }
    }
  };

  return (
    <T2Panel
      icon={FileText}
      title="Quote notes summary"
      testid="panel-quote-notes-summary"
      right={
        <div style={{ display: "inline-flex", alignItems: "center", gap: 12 }}>
          <button type="button" onClick={copyAll} data-testid="quote-summary-copy"
            className="t2-mono" title="Copy the whole summary as text"
            style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 9.5, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--t2-teal-ink)", background: "rgba(47,200,203,0.10)", border: "1px solid rgba(47,200,203,0.30)", borderRadius: 5, padding: "3px 8px", cursor: "pointer" }}>
            <Copy className="w-3 h-3" /> Copy
          </button>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={includeOnPdf}
              onChange={(e) => patch({ includeSummaryOnPdf: e.target.checked })}
              data-testid="delivery-include-summary-on-pdf" />
            <span className="t2-mono" style={{
              fontSize: 9.5, textTransform: "uppercase", letterSpacing: ".06em",
              color: includeOnPdf ? "var(--t2-teal-ink)" : "var(--t2-grey3)",
            }}>
              Include on PDF
            </span>
          </label>
        </div>
      }
    >
      <div style={{
        padding: "10px 12px", borderRadius: 6,
        background: "rgba(255,255,255,0.02)",
        border: "1px solid rgba(255,255,255,0.06)",
        fontFamily: "var(--t2-mono)", fontSize: 11, color: "var(--t2-grey1)",
        lineHeight: 1.7, whiteSpace: "pre-wrap",
      }} data-testid="delivery-summary-preview">
        {summaryText}
      </div>

      <CompletenessBar filled={comp.filled} total={comp.total} pct={comp.pct} />
      {missing.length > 0 ? (
        <div style={{
          marginTop: 8, padding: "6px 10px", borderRadius: 6,
          background: "rgba(219, 96, 82, 0.08)",
          border: "1px dashed rgba(219, 96, 82, 0.30)",
          fontSize: 10.5, color: "var(--t2-red-ink)",
          display: "flex", alignItems: "flex-start", gap: 6,
        }} data-testid="delivery-summary-liability-note">
          <Info className="w-3.5 h-3.5" style={{ marginTop: 1 }} />
          <span>
            <b>Fields blank:</b> {missing.join(", ")}.<br />
            If left blank on the PDF, the customer accepts these are unknown and is liable for any mis-delivery.
          </span>
        </div>
      ) : (
        <div style={{
          marginTop: 8, padding: "6px 10px", borderRadius: 6,
          background: "rgba(74, 191, 118, 0.08)",
          border: "1px solid rgba(74, 191, 118, 0.30)",
          fontSize: 10.5, color: "var(--t2-ok-ink)",
          fontFamily: "var(--t2-mono)", textTransform: "uppercase", letterSpacing: ".05em",
        }}>
          ✓ All fields captured — safe to send.
        </div>
      )}
    </T2Panel>
  );
}

function CompletenessBar({ filled, total, pct }) {
  const color = `var(--t2-${completionTone(pct)}-ink)`;
  return (
    <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10 }} data-testid="quote-notes-completeness"
      title="Logged per account manager on every save, for the Management reports">
      <span className="t2-mono" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--t2-grey3)", whiteSpace: "nowrap" }}>Quote completion</span>
      <div style={{ flex: 1, height: 6, borderRadius: 99, background: "rgba(255,255,255,0.06)", overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, transition: "width .3s ease" }} />
      </div>
      <span className="t2-mono" style={{ fontSize: 11, fontWeight: 700, color }} data-testid="quote-notes-completeness-pct">{filled}/{total} · {pct}%</span>
    </div>
  );
}

// ─── Shared: build the summary body used by both the panel + the snapshot ──
function buildSummaryStrings(quote, delivery = {}, vehicle, billing) {
  const cust = quote?.Customer || {};
  const cName = [cust.firstName, cust.lastName].filter(Boolean).join(" ") || cust.company || "—";
  const cMobile = cust.phone || "—";
  const src = delivery.addressSource || "invoice";
  const da = src === "invoice" ? (billing || cust.billingAddress || {}) : (delivery.deliveryAddress || {});
  const addr = [da.line1, da.line2, da.city, da.postcode].filter(Boolean).join(", ")
    || [cust.address1, cust.city, cust.postcode].filter(Boolean).join(", ") || "—";
  const build = quote?.BuildType || "—";
  const tl = quote?.Timeline || {};
  const target = tl.asap ? "ASAP" : tl.targetDate ? new Date(tl.targetDate).toLocaleDateString("en-GB") : "—";

  const ppl = onsiteAnswer(delivery, "people");
  const offloadLine = `Forks on site: ${onsiteLabel(onsiteAnswer(delivery, "forklift"))}   |   ` +
    `Handball: ${ppl === "yes" ? `YES — ${Number(delivery.peopleOnSite) || 1} people` : onsiteLabel(ppl)}`;
  const kerb = onsiteAnswer(delivery, "kerbside"), park = onsiteAnswer(delivery, "parking");
  const weightLine = delivery.estimatedWeightKg
    ? `${delivery.estimatedWeightKg} kg (${vehicle?.weight_label || "—"} · ${vehicle?.vehicle || "—"})`
    : "—";

  const summaryText =
`${quote?.ProjectRef ? `Project:           ${quote.ProjectRef}\n` : ""}Customer:          ${cName}   |   Mobile: ${cMobile}
Delivery address:  ${addr}
Build type:        ${build}
Target date:       ${target}
Delivery method:   ${delivery.method || "—"}
Access:            ${delivery.access || "—"}
Parking:           ${delivery.parking || "—"}${park === "yes" ? "  [RESTRICTED]" : park === "unsure" ? "  [RESTRICTIONS UNSURE]" : ""}
Offload:           ${offloadLine}${kerb === "yes" ? "  [KERBSIDE ONLY]" : kerb === "unsure" ? "  [KERBSIDE UNSURE]" : ""}
Weight:            ${weightLine}
Delivery window:   ${delivery.deliveryWindow || "—"}
Site contact:      ${delivery.siteContactName || "—"} ${delivery.siteContactPhone ? " · " + delivery.siteContactPhone : ""}
Notes:             ${delivery.notes || "—"}`;

  return { summaryText };
}

// ─── Snapshot builder (used by the orange Save quote button) ────────────
// Returns a multi-line string suitable for POST /quotes/{id}/notes.
// Adds financial totals on top of the customer-facing summary.
export function buildQuoteSnapshot({ quote, delivery, subtotal, transition, event }) {
  const vehicle = suggestVehicle(delivery?.estimatedWeightKg);
  const { summaryText } = buildSummaryStrings(quote, delivery || {}, vehicle);
  const comp = quoteCompletion(quote, delivery || {});
  const web = websiteDiscount(quote?.LineItems || []);

  const lineCount = (quote?.LineItems || []).length;
  const totalCost = (quote?.LineItems || []).reduce(
    (s, it) => s + (Number(it.cost) || 0) * (Number(it.qty) || 0), 0,
  );
  const rebatePct = Number(quote?.RebatePct) || 0;
  const rebateAbs = totalCost * (rebatePct / 100);
  const netCost   = totalCost - rebateAbs;
  const deliveryCost = Number(delivery?.cost) || 0;
  const vat   = (subtotal + deliveryCost) * 0.20;
  const grand = subtotal + deliveryCost + vat;
  const marginPct = subtotal > 0 ? ((subtotal - netCost) / subtotal) * 100 : 0;

  const ts = new Date().toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

  return (
`QUOTE SUMMARY SNAPSHOT — ${ts}${transition ? `\nStatus change:     ${transition}` : event ? `\nEvent:             ${event}` : ""}
Ref:               ${quote?.QuoteRef || "—"}   |   Status: ${quote?.Status || "—"}
Lines:             ${lineCount}   |   Subtotal (ex VAT): ${GBP(subtotal)}
Delivery:          ${delivery?.method || "Our"} · ${deliveryCost > 0 ? GBP(deliveryCost) : "FREE"}${delivery?.band ? " · band " + delivery.band : ""}
Rebate:            ${rebatePct}%${rebatePct > 0 ? " (−" + GBP(rebateAbs) + ")" : ""}
Real margin:       ${marginPct.toFixed(1)}%
Discount vs web:   ${web.count ? `${GBP(web.disc)} (${web.pct.toFixed(1)}% off website)` : "—"}
Completion:        ${comp.pct}% (${comp.filled}/${comp.total})${comp.missing.length ? " · missing: " + comp.missing.join(", ") : ""}
Grand total:       ${GBP(grand)}
─────────────────────────────────────────
${summaryText}`
  );
}
